import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Form, Input, DatePicker, TimePicker, Button, Typography, Flex, message } from "antd";
import {
  PhoneOutlined,
  VideoCameraOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import { createActivity } from "../../../services/activityService";

const { Text, Title } = Typography;
const { TextArea } = Input;

const ACTIVITY_TYPES = [
  { key: "call", label: "Call", icon: PhoneOutlined, color: "#10b981", bg: "#ecfdf5", border: "#a7f3d0" },
  { key: "meeting", label: "Meeting", icon: VideoCameraOutlined, color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
  { key: "task", label: "Task", icon: CheckSquareOutlined, color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
];

const generateInitialTitle = (type, leadName, companyName) => {
  const entity = leadName || companyName || "Lead";
  const typeLabel = type === "call" ? "Call" : type === "meeting" ? "Meeting" : "Task";
  return `Follow-up ${typeLabel} with ${entity}`;
};

function ScheduleActivityModal({
  open,
  onCancel,
  onSuccess,
  lead = null,
  companyName = "",
  defaultType = "call",
  defaultDate = null,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(defaultType || "call");

  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";
  const employeeId = user?._id || user?.id || getCookie("employeeId") || getCookie("userId") || "";

  const leadContactName = lead?.contactName || lead?.clientName || lead?.leadName || "";
  const leadCompanyName = lead?.leadCompanyName || lead?.companyName || companyName || "";
  const leadPhone = lead?.contactNumber || lead?.phone || "";
  const leadEmail = lead?.directorEmailAddress || lead?.companyEmail || lead?.email || "";

  const handleResetForm = useCallback(() => {
    const initType = defaultType || "call";
    setSelectedType(initType);
    form.setFieldsValue({
      type: initType,
      date: defaultDate ? dayjs(defaultDate) : dayjs().add(1, "day"),
      time: dayjs("10:00 AM", "hh:mm A"),
      title: generateInitialTitle(initType, leadContactName, leadCompanyName),
      description: "",
    });
  }, [defaultType, defaultDate, leadContactName, leadCompanyName, form]);

  useEffect(() => {
    if (open) {
      handleResetForm();
    }
  }, [open, handleResetForm]);

  const handleTypeChange = (typeKey) => {
    setSelectedType(typeKey);
    form.setFieldValue("type", typeKey);
    const currentTitle = form.getFieldValue("title");
    if (!currentTitle || currentTitle.startsWith("Follow-up ")) {
      form.setFieldValue("title", generateInitialTitle(typeKey, leadContactName, leadCompanyName));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const datePart = values.date.format("YYYY-MM-DD");
      const timePart = values.time ? values.time.format("HH:mm:ss") : "10:00:00";
      const activityDate = new Date(`${datePart}T${timePart}`);

      const payload = {
        employeeId,
        leadId: lead?._id || undefined,
        type: values.type || selectedType,
        title: values.title.trim(),
        description: values.description ? values.description.trim() : "",
        activityDate: activityDate.toISOString(),
      };

      const res = await createActivity(effectiveCompanyCode, payload);

      if (res?.success !== false) {
        message.success(`Activity scheduled successfully!`);
        onSuccess?.(res?.data || payload);
        onCancel();
      } else {
        message.error(res?.message || "Failed to schedule activity.");
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      console.error("[ScheduleActivityModal] Error:", err);
      message.error("Failed to schedule activity. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const activeTypeConfig = useMemo(
    () => ACTIVITY_TYPES.find((t) => t.key === selectedType) || ACTIVITY_TYPES[0],
    [selectedType]
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={null}
      footer={null}
      width={480}
      destroyOnClose
      centered
    >
      <div style={{ padding: "4px 0" }}>
        {/* Header */}
        <Flex gap={12} align="center" style={{ marginBottom: 16, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              backgroundColor: activeTypeConfig.bg,
              border: `1px solid ${activeTypeConfig.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: activeTypeConfig.color,
              fontSize: 18,
            }}
          >
            <activeTypeConfig.icon />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: "#0f172a", fontSize: "1.05rem" }}>
              Schedule Activity
            </Title>
            <Text type="secondary" style={{ fontSize: "0.8rem" }}>
              Follow-up with client
            </Text>
          </div>
        </Flex>

        {/* Lead Context Summary */}
        {(leadContactName || leadCompanyName) && (
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 14,
            }}
          >
            <Flex justify="space-between" align="center" wrap="wrap" gap={6}>
              <Flex align="center" gap={6}>
                <UserOutlined style={{ color: "#2459d3", fontSize: 13 }} />
                <Text strong style={{ color: "#1e293b", fontSize: "0.85rem" }}>
                  {leadContactName || "Lead Contact"}
                </Text>
                {leadCompanyName && (
                  <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                    &bull; {leadCompanyName}
                  </Text>
                )}
              </Flex>
              {(leadPhone || leadEmail) && (
                <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                  {leadPhone || leadEmail}
                </Text>
              )}
            </Flex>
          </div>
        )}

        <Form form={form} layout="vertical" initialValues={{ type: "call" }} requiredMark={false}>
          {/* Activity Type Selector */}
          <Form.Item label={<Text strong style={{ color: "#334155", fontSize: "0.85rem" }}>Activity Type</Text>} style={{ marginBottom: 14 }}>
            <Flex gap={8}>
              {ACTIVITY_TYPES.map((t) => {
                const isSelected = selectedType === t.key;
                const IconComponent = t.icon;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleTypeChange(t.key)}
                    style={{
                      flex: 1,
                      padding: "8px 6px",
                      borderRadius: 8,
                      border: isSelected ? `2px solid ${t.color}` : "1px solid #e2e8f0",
                      backgroundColor: isSelected ? t.bg : "#ffffff",
                      color: isSelected ? t.color : "#64748b",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: "0.85rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <IconComponent style={{ fontSize: 15 }} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </Flex>
          </Form.Item>

          {/* Activity Title */}
          <Form.Item
            name="title"
            label={<Text strong style={{ color: "#334155", fontSize: "0.85rem" }}>Title</Text>}
            rules={[{ required: true, message: "Please enter title" }]}
            style={{ marginBottom: 14 }}
          >
            <Input
              placeholder="e.g. Follow-up Call"
              style={{ borderRadius: 8, height: 36 }}
            />
          </Form.Item>

          {/* Date & Time */}
          <Flex gap={12} style={{ marginBottom: 14 }}>
            <Form.Item
              name="date"
              label={
                <Text strong style={{ color: "#334155", fontSize: "0.85rem" }}>
                  <CalendarOutlined style={{ marginRight: 4 }} /> Date
                </Text>
              }
              rules={[{ required: true, message: "Please select date" }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <DatePicker
                format="DD MMM YYYY"
                style={{ width: "100%", borderRadius: 8, height: 36 }}
              />
            </Form.Item>

            <Form.Item
              name="time"
              label={
                <Text strong style={{ color: "#334155", fontSize: "0.85rem" }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} /> Time
                </Text>
              }
              rules={[{ required: true, message: "Please select time" }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <TimePicker
                format="hh:mm A"
                use12Hours
                style={{ width: "100%", borderRadius: 8, height: 36 }}
              />
            </Form.Item>
          </Flex>

          {/* Description */}
          <Form.Item
            name="description"
            label={<Text strong style={{ color: "#334155", fontSize: "0.85rem" }}>Description (Optional)</Text>}
            style={{ marginBottom: 18 }}
          >
            <TextArea
              rows={3}
              placeholder="Notes or details for this activity..."
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          {/* Footer Actions */}
          <Flex justify="flex-end" gap={10} style={{ borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
            <Button onClick={onCancel} style={{ borderRadius: 8, height: 36, padding: "0 16px" }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              style={{
                borderRadius: 8,
                height: 36,
                padding: "0 20px",
                fontWeight: 600,
                backgroundColor: "#2459d3",
                borderColor: "#2459d3",
              }}
            >
              Schedule Activity
            </Button>
          </Flex>
        </Form>
      </div>
    </Modal>
  );
}

export default ScheduleActivityModal;
